import { InstrumentT } from '@/common/types'
import { H2 } from '@/common/ui/h2'
import useTradeStore from '@/trading/tradeStore'
import useAccountStore from '@/account/accountStore'
import { Button } from '@/common/ui/button'
import { Info, Undo2 } from 'lucide-react'
import { FC, useEffect } from 'react'
import { usePlaceBuyOrder, usePlaceSellOrder } from '@/trading/tradingQueries'
import { analytics } from '@/common/analytics/analytics'
import { BuyOrderSubmittedAnalytics } from '@/common/analytics/events/orders/BuyOrderSubmittedAnalytics'
import { ReviewItems } from './revewItems'
import { Alert, AlertDescription } from '@/common/ui/alert.tsx'
import { SupportedCampaigns } from '@/campaigns/supportedCampaigns.tsx'
import { usePledgeForCampaign } from '@/campaigns/campaignsQueries.ts'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { useOrderSource } from '@/common/context/useOrderSource'

type Props = {
    instrument: InstrumentT
    pledges?: { [key: string]: number } | null
    hasApprovedTradingAccount: boolean
    onBackToOrder: () => void
    onSubmit: () => void
}
export const TradeReview: FC<Props> = ({ instrument, hasApprovedTradingAccount, onBackToOrder, onSubmit }) => {
    const { orderSource, clearOrderSource } = useOrderSource()
    const pledgeCampaign = usePledgeForCampaign()
    const isPledgedAndCampaignsForSecurity = useTradeStore((state) => state.isPledgedAndCampaignsForSecurity)
    const campaignsForSecurity = useTradeStore((state) => state.campaignsForSecurity)
    const ethicalTags = useTradeStore((state) => state.ethicalTags)
    const numPositiveTags = useTradeStore((state) => state.numPositiveTags)
    const numNegativeTags = useTradeStore((state) => state.numNegativeTags)
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const placeBuyOrder = usePlaceBuyOrder()
    const placeSellOrder = usePlaceSellOrder()
    const action = useTradeStore((state) => state.orderAction)
    const requestId = useTradeStore((state) => state.requestId)
    const orderType = useTradeStore((state) => state.orderType)
    const limitPrice = useTradeStore((state) => state.limitPrice)
    const orderExpiry = useTradeStore((state) => state.orderExpiry)
    const numberOfShares = useTradeStore((state) => state.numberOfShares)
    const price = useTradeStore((state) => state.instrumentPrice)
    const hasSixthSenseProfile = useTradeStore((state) => state.hasSixthSenseProfile)
    const promotion = useTradeStore((state) => state.promotion)

    useEffect(() => {
        if (placeBuyOrder.isSuccess || placeSellOrder.isSuccess) {
            clearOrderSource()
            onSubmit()
        }
    }, [placeBuyOrder.isSuccess, placeSellOrder.isSuccess, clearOrderSource, onSubmit])

    if (!price || !numberOfShares || !action) return null
    const sharesAmount = orderType === 'LIMIT' ? limitPrice : numberOfShares * price

    const placeOrder = () => {
        if (!requestId || !numberOfShares || !orderType || !activeAccountId) return
        submitPledgeIfOptedInForActiveCampaigns()

        if (action === 'BUY') {
            placeBuyOrder.mutate({
                requestId,
                orderType,
                requestedPrice: orderType === 'LIMIT' ? limitPrice : undefined,
                units: numberOfShares,
                validity: orderExpiry,
                securityCode: instrument.securityCode,
                promotionId: promotion?.promotionId,
                accountId: activeAccountId,
            })
            BuyOrderSubmittedAnalytics.trackWithUtm({
                request_id: requestId,
                security_code: instrument.securityCode,
                type: orderType,
                requested_price: sharesAmount,
                last_price: price,
                number_of_shares: numberOfShares,
                validity: orderExpiry,
                isPledged: isPledgedAndCampaignsForSecurity,
                has_campaign: Boolean(campaignsForSecurity?.length),
                ethicalTags: ethicalTags,
                numPositiveTags: numPositiveTags,
                numNegativeTags: numNegativeTags,
                hasSixthSenseProfile: hasSixthSenseProfile,
                promotionId: promotion?.promotionId,
                promotion: promotion?.promotionType,
                order_from: orderSource.orderFrom,
                order_entry_point: orderSource.orderEntryPoint,
                accountId: activeAccountId,
            })
        }
        if (action === 'SELL') {
            placeSellOrder.mutate({
                requestId,
                orderType,
                requestedPrice: orderType === 'LIMIT' ? limitPrice : undefined,
                units: numberOfShares,
                validity: orderExpiry,
                securityCode: instrument.securityCode,
                promotionId: promotion?.promotionId,
                accountId: activeAccountId,
            })
            analytics.track(ANALYTICS_EVENTS.TRADING.SELL_ORDER_SUBMITTED, {
                request_id: requestId,
                security_code: instrument.securityCode,
                type: orderType,
                requested_price: sharesAmount,
                last_price: price,
                number_of_shares: numberOfShares,
                hasCampaign: campaignsForSecurity && campaignsForSecurity?.length > 0,
                validity: orderExpiry,
                ethicalTags: ethicalTags,
                numPositiveTags: numPositiveTags,
                numNegativeTags: numNegativeTags,
                hasSixthSenseProfile: hasSixthSenseProfile,
                promotionId: promotion?.promotionId,
                promotion: promotion?.promotionType,
                accountId: activeAccountId,
            })
        }
    }

    const submitPledgeIfOptedInForActiveCampaigns = () => {
        if (!isPledgedAndCampaignsForSecurity) {
            return
        }
        campaignsForSecurity?.forEach((campaign) => {
            pledgeCampaign.mutate({ campaignId: campaign.campaignId, securityCode: instrument.securityCode })
        })
    }

    return (
        <div>
            <H2 className="text-center">Review {action === 'BUY' ? 'Buy' : 'Sell'} Order</H2>

            {!hasApprovedTradingAccount && (
                <Alert className="p-4 flex gap-4 justify-start mb-4">
                    <Info size={24} strokeWidth={2} className="flex-shrink-0" />
                    <div>
                        <AlertDescription>
                            Your trading account is still being set up (this usually takes 20 minutes during business
                            hours). Buy orders will be submitted during market hours.
                        </AlertDescription>
                    </div>
                </Alert>
            )}

            <ReviewItems instrument={instrument} />

            <SupportedCampaigns />

            <p className="w-full max-w-[493px] text-black text-xs font-normal font-['Inter'] leading-[18px] my-5">
                Trade confirmation will record brokerage charges and SIX does not accept any responsibility for market
                movements.{' '}
                {action === 'BUY' && (
                    <span>
                        You must ensure sufficient funds are available to cover the total consideration of the order.
                    </span>
                )}
            </p>

            <div className="flex justify-between items-center mt-8">
                <Button variant="link" onClick={onBackToOrder} className="m-0 p-0">
                    <Undo2 size={16} /> Amend
                </Button>
                <Button
                    loading={placeBuyOrder.isPending || placeSellOrder.isPending}
                    variant="secondary"
                    size="lg"
                    onClick={placeOrder}
                >
                    Submit Order
                </Button>
            </div>
        </div>
    )
}
